import { Controller } from "@hotwired/stimulus"

// The head of a document (assistant_documents/_header): its title menu. Copy as
// Markdown, folding the version chips open, renaming in place, and who can open
// it. Everything that changes the document is one PATCH to the document, and
// afterwards the view reloads itself - the side panel by asking itself to open
// the same document again, the full page by reloading - so what is on screen is
// always what the server holds.
export default class extends Controller {
    static targets = ["title", "access", "accessNow", "project", "error", "copyLabel", "linkLabel"]
    static values = { shareUrl: String, updateUrl: String, markdownUrl: String, panelUrl: String, panel: Boolean, icon: { type: String, default: "bi-file-earmark-word" }, label: { type: String, default: "Document" } }

    async copyMarkdown() {
        try {
            const response = await fetch(this.markdownUrlValue, { headers: { Accept: "text/markdown" }, credentials: "same-origin" })
            if (!response.ok) throw new Error(response.status)
            await navigator.clipboard.writeText(await response.text())
            this._flashCopy("Copied")
        } catch (_error) {
            this._flashCopy("Couldn't copy")
        }
    }

    async copyLink() {
        try {
            await navigator.clipboard.writeText(this.shareUrlValue)
            this._flashLink("Link copied")
        } catch (_error) {
            this._flashLink("Couldn't copy")
        }
        return false
    }

    // The chips live in the body, apart from this bar (which may sit in the side
    // panel's head), so the request goes out as an event.
    // On a phone the header has no room for a Share button; the title menu opens the
    // same panel instead (after this click has finished, or the panel would close at once).
    openShare() {
        setTimeout(() => this.element.querySelector(".document-share-button")?.click(), 0)
    }

    toggleVersions() {
        window.dispatchEvent(new CustomEvent("document:toggle-versions"))
    }

    toggleComments() {
        window.dispatchEvent(new CustomEvent("document:toggle-comments"))
    }

    toggleAccess() {
        if (this.hasAccessTarget) this.accessTarget.hidden = !this.accessTarget.hidden
    }

    startRename() {
        if (!this.hasTitleTarget || this._input) return

        const current = this.titleTarget.textContent.trim()
        const input = document.createElement("input")
        input.type = "text"
        input.className = "document-menu-rename"
        input.value = current
        input.maxLength = 200
        input.setAttribute("aria-label", "Document name")
        input.addEventListener("keydown", (event) => {
            if (event.key === "Enter") {
                event.preventDefault()
                this._finishRename(input, current)
            } else if (event.key === "Escape") {
                this._cancelRename(input)
            }
        })
        input.addEventListener("blur", () => this._cancelRename(input))
        this._input = input
        this._titleWasHidden = this.titleTarget.hidden
        this.titleTarget.hidden = true
        this.titleTarget.after(input)
        input.focus()
        input.select()
    }

    async saveAccess() {
        if (!this.hasProjectTarget) return

        this._showError(null)
        const ok = await this._patch({ project_id: this.projectTarget.value })
        if (ok) this._reload()
    }

    async _finishRename(input, current) {
        const title = input.value.trim()
        if (!title || title === current) return this._cancelRename(input)

        input.disabled = true
        const ok = await this._patch({ title })
        if (ok) {
            this._reload(title)
        } else {
            input.disabled = false
            input.focus()
        }
    }

    _cancelRename(input) {
        if (this._input !== input) return

        this._input = null
        input.remove()
        if (this.hasTitleTarget) this.titleTarget.hidden = this._titleWasHidden === true
    }

    async _patch(body) {
        try {
            const response = await fetch(this.updateUrlValue, {
                method: "PATCH",
                credentials: "same-origin",
                headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this._csrf() },
                body: JSON.stringify(body)
            })
            if (response.ok) return true

            const data = await response.json().catch(() => ({}))
            this._showError(data.error || "That didn't save. Try again.")
        } catch (_error) {
            this._showError("That didn't save. Check your connection and try again.")
        }
        return false
    }

    _reload(title) {
        if (this.panelValue) {
            window.dispatchEvent(new CustomEvent("jane:open-panel", {
                detail: { url: this.panelUrlValue, title: title || this.titleTarget.textContent.trim(), icon: this.iconValue, label: this.labelValue }
            }))
        } else {
            window.location.reload()
        }
    }

    _showError(message) {
        if (!this.hasErrorTarget) return

        this.errorTarget.textContent = message || ""
        this.errorTarget.hidden = !message
    }

    _flashLink(text) {
        if (!this.hasLinkLabelTarget) return

        const original = this.linkLabelTarget.textContent
        this.linkLabelTarget.textContent = text
        setTimeout(() => { this.linkLabelTarget.textContent = original }, 1500)
    }

    _flashCopy(text) {
        if (!this.hasCopyLabelTarget) return

        const original = this.copyLabelTarget.textContent
        this.copyLabelTarget.textContent = text
        setTimeout(() => { this.copyLabelTarget.textContent = original }, 1500)
    }

    _csrf() {
        return document.querySelector("meta[name='csrf-token']")?.content || ""
    }
}
